import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {Form, Formik} from 'formik';
import AppAnimate from 'main/core/AppAnimate';
import AppCard from 'main/core/AppCard';
import AppGridContainer from 'main/core/AppGridContainer';
import {useWidget} from 'main/utility/hooks/useWidget';
import {
  evaluatePath,
  getFormInitialValues,
  getValidationRules,
} from 'main/utility/LayoutUtil';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  fetchWidgetSuccess,
  getWidgetData,
  saveWidgetData,
  saveWidgetDataWithFile,
} from 'redux/actions';
import {
  UiWiseActionType,
  UiWiseFormFieldType,
} from 'shared/constants/UiWiseConst';
import WidgetAction from '../WidgetAction';
import WidgetTitle from '../WidgetTitle';
import {getFormField} from './FormFields';

const FormWidget = ({widgetSpec, params}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    widgetid,
    idfieldname,
    fields,
    actions,
    widgettitle,
    widgettype,
    fieldlayout,
  } = widgetSpec;

  const validationSchema = getValidationRules(fields);
  const {widgetData, isLoading, isSuccess} = useWidget(widgetid);
  const idFieldValue = widgetData?.[idfieldname];
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const paramID = params?.[idfieldname];
  const isNewRecord = paramID === '_NEW_';

  const submitAction = useMemo(() => {
    const action = actions.find(
      (action) => UiWiseActionType.SUBMIT === action.actiontype,
    );
    if (!action) return null;

    const newSuccesspath = evaluatePath(action.successpath, {
      ...params,
      ...widgetData,
    });

    return {
      ...action,
      successpath: newSuccesspath,
    };
  }, [actions, widgetData, params]);

  const initialValues = useMemo(() => {
    const baseValues = getFormInitialValues(fields, widgetData);

    // Merge the base values with your URL params, order matters
    const merged = isNewRecord
      ? {...baseValues, ...params}
      : {...params, ...baseValues};

    // Remove _NEW_
    if (isNewRecord && merged[idfieldname]) {
      delete merged[idfieldname];
    }

    return merged;
  }, [fields, widgetData, params, isNewRecord, idfieldname]);

  useEffect(() => {
    if (isNewRecord) {
      dispatch(fetchWidgetSuccess(widgetid, null));
    } else if (!isFormSubmitted) {
      dispatch(getWidgetData(widgetid, params));
    }
  }, [
    dispatch,
    widgetid,
    isNewRecord,
    isFormSubmitted,
    JSON.stringify(params),
  ]);

  const submitWidget = (data) => {
    setIsFormSubmitted(true);

    const hasFile = 'fileInputFields' in data || 'filesRemoved' in data;

    if (hasFile) {
      dispatch(saveWidgetDataWithFile(widgetid, data));
    } else {
      dispatch(saveWidgetData(widgetid, data));
    }
  };

  useEffect(() => {
    // Wait for the success from Save API and show the success alert before redirecting.
    const validSuccessPath =
      submitAction?.successpath && !submitAction.successpath.includes('${');

    if (isSuccess && isFormSubmitted && validSuccessPath) {
      const timer = setTimeout(() => {
        navigate(submitAction.successpath);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, isFormSubmitted, navigate, submitAction]);

  return (
    <AppAnimate animation='transition.slideUpIn' delay={200}>
      <AppCard>
        <WidgetTitle
          title={widgettitle}
          widgetType={widgettype}
          idFieldValue={idFieldValue ?? 'NEW'}
        />

        <Formik
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (data) => submitWidget(data)}
        >
          <Form autoComplete='off'>
            <fieldset disabled={isLoading} style={{border: 'none', padding: 0}}>
              {/* 1. Render Label/Hidden type Fields first (No Grid Wrapper) */}
              {fields
                .filter(
                  (field) => field.fieldtype === UiWiseFormFieldType.HIDDEN,
                )
                .map((field, i) => (
                  <React.Fragment key={`${field.name}-${i}`}>
                    {getFormField(widgetid, field)}
                  </React.Fragment>
                ))}

              <AppGridContainer
                spacing={6}
                sx={{
                  flexDirection: fieldlayout,
                  '& .MuiGrid-item': {
                    minHeight: '55px',
                  },
                  mt: 3,
                }}
              >
                {fields
                  .filter(
                    (field) => field.fieldtype !== UiWiseFormFieldType.HIDDEN,
                  )
                  .map((field, i) => (
                    <Grid item key={i} xs={field.xs} md={field.md}>
                      {getFormField(widgetid, field)}
                    </Grid>
                  ))}

                {actions?.length > 0 && (
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1.5, // Adds consistent 12px spacing between buttons
                        '& > *': {
                          flex: '0 0 auto', // Prevents buttons from growing or shrinking
                        },
                      }}
                    >
                      {actions.map((action, i) => (
                        <WidgetAction
                          key={i}
                          action={action}
                          data={{
                            ...params,
                            ...widgetData,
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </AppGridContainer>
            </fieldset>
          </Form>
        </Formik>
      </AppCard>
    </AppAnimate>
  );
};

FormWidget.propTypes = {
  widgetSpec: PropTypes.object.isRequired,
  params: PropTypes.object,
};

export default FormWidget;
